import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Timer, TrendingUp } from 'lucide-react';
import { api, type StudentRiskShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Phase 6 — Analytics tab. Renders Risk Radar + Study Time (per-student
 * Study Room aggregate). Course-pulse digest + sentiment trends + Living
 * Pulse get their own panels in this tab as they ship.
 */
export function AnalyticsTab({
  token,
  courseId,
  canEdit,
}: {
  token: string;
  courseId: string;
  canEdit: boolean;
}) {
  if (!canEdit) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        Analytics is visible to instructors and TAs only.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <RiskRadar token={token} courseId={courseId} />
      <StudyTimePanel token={token} courseId={courseId} />
    </div>
  );
}

function RiskRadar({ token, courseId }: { token: string; courseId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['risks', courseId],
    queryFn: () => api.risks.listForCourse(token, courseId),
    staleTime: 60_000,
    enabled: token.length > 0,
  });

  if (isLoading) {
    return (
      <Panel>
        <div className="text-sm text-muted-foreground">Computing risk scores…</div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <div className="text-sm text-destructive">Couldn't load risk data.</div>
      </Panel>
    );
  }

  const risks = data?.risks ?? [];
  if (risks.length === 0) {
    return (
      <Panel>
        <div className="text-sm text-muted-foreground">
          No enrolled students yet — Risk Radar will populate once students join.
        </div>
      </Panel>
    );
  }

  const reds = risks.filter((r) => r.band === 'red').length;
  const ambers = risks.filter((r) => r.band === 'amber').length;
  const greens = risks.filter((r) => r.band === 'green').length;

  return (
    <Panel>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-accent" aria-hidden />
          <h3 className="font-display text-base font-semibold">Risk Radar</h3>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <BandPill band="red" count={reds} />
          <BandPill band="amber" count={ambers} />
          <BandPill band="green" count={greens} />
        </div>
      </header>
      <ul className="divide-y divide-border">
        {risks.map((r) => (
          <RiskRow key={r.profileId} risk={r} />
        ))}
      </ul>
    </Panel>
  );
}

function RiskRow({ risk: r }: { risk: StudentRiskShape }) {
  return (
    <li className="grid grid-cols-[1fr_auto_auto] items-center gap-4 p-3 sm:grid-cols-[1fr_2fr_auto_auto]">
      <div>
        <div className="text-sm font-semibold">{r.fullName}</div>
        <div className="text-xs text-muted-foreground">{r.email}</div>
      </div>
      <div className="hidden text-xs text-muted-foreground sm:block">
        <span className="mr-3">
          {r.signals.daysSinceLastActivity === null
            ? 'No activity yet'
            : `${r.signals.daysSinceLastActivity}d since attempt`}
        </span>
        {r.signals.missedAssignments > 0 && (
          <span className="mr-3 inline-flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> {r.signals.missedAssignments} missed
          </span>
        )}
        {r.signals.averageScorePct !== null && (
          <span className="inline-flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> {r.signals.averageScorePct}% avg
          </span>
        )}
      </div>
      <div className="text-right text-sm font-mono font-bold">{r.score}</div>
      <BandPill band={r.band} />
    </li>
  );
}

function BandPill({ band, count }: { band: 'red' | 'amber' | 'green'; count?: number }) {
  const cls =
    band === 'red'
      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
      : band === 'amber'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
        cls,
      )}
    >
      {count !== undefined ? `${count} ${band}` : band}
    </span>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return <section className="rounded-2xl border border-border bg-card">{children}</section>;
}

/**
 * Aggregate Study Room time per student — joined client-side with the
 * risk-radar query for names + emails (we already pay for that fetch).
 * Empty until students actually use the Study Room.
 */
function StudyTimePanel({ token, courseId }: { token: string; courseId: string }) {
  const timesQuery = useQuery({
    queryKey: ['study-times', courseId],
    queryFn: () => api.courses.studyTimes(token, courseId),
    staleTime: 60_000,
    enabled: token.length > 0,
  });
  // Reuse the risks query (already populated for this courseId by the
  // sibling RiskRadar) so we don't need name/email on /study-times.
  const risksQuery = useQuery({
    queryKey: ['risks', courseId],
    queryFn: () => api.risks.listForCourse(token, courseId),
    staleTime: 60_000,
    enabled: token.length > 0,
  });

  const times = timesQuery.data?.times ?? [];
  const risks = risksQuery.data?.risks ?? [];
  const nameByProfile = new Map(risks.map((r) => [r.profileId, r] as const));

  // Show every enrolled student even if they haven't studied yet — gives
  // instructors a sense of who's not using the room.
  const rows = times.map((t) => ({
    ...t,
    name: nameByProfile.get(t.profileId)?.fullName ?? 'Unknown',
    email: nameByProfile.get(t.profileId)?.email ?? '',
  }));
  rows.sort((a, b) => b.studyTimeMs - a.studyTimeMs);

  if (timesQuery.isLoading) {
    return (
      <Panel>
        <div className="p-4 text-sm text-muted-foreground">Loading study time…</div>
      </Panel>
    );
  }

  if (rows.length === 0) {
    return (
      <Panel>
        <header className="flex items-center gap-2 border-b border-border p-4">
          <Timer className="h-4 w-4 text-accent" aria-hidden />
          <h3 className="font-display text-base font-semibold">Study Room time</h3>
        </header>
        <div className="p-4 text-sm text-muted-foreground">
          No Study Room sessions yet. Time accumulates when students open the room and ask
          questions; a session credits its time to the gradebook when ended.
        </div>
      </Panel>
    );
  }

  const totalMs = rows.reduce((sum, r) => sum + r.studyTimeMs, 0);

  return (
    <Panel>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4 text-accent" aria-hidden />
          <h3 className="font-display text-base font-semibold">Study Room time</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          Total: <span className="font-semibold text-foreground">{formatMs(totalMs)}</span>
        </span>
      </header>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.profileId} className="grid grid-cols-[1fr_auto] items-center gap-4 p-3">
            <div>
              <div className="text-sm font-semibold">{r.name}</div>
              {r.email && <div className="text-xs text-muted-foreground">{r.email}</div>}
            </div>
            <div className="text-right text-sm font-mono">
              {r.studyTimeMs > 0 ? (
                formatMs(r.studyTimeMs)
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** ms → "1h 23m" / "12m" / "—" for zero. */
function formatMs(ms: number): string {
  if (ms <= 0) return '0m';
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
